import {useMemo} from 'react'
import {UseMutationResult} from '@tanstack/react-query'
import {AxiosError, AxiosResponse} from 'axios'
import {UseFormReturn, useWatch} from 'react-hook-form'
import {ObjectKeys} from '@/utilities/helpers'
import {MedicalReportValidationSchema} from '../formSchemas'
import {HttpMedicalReportPayload} from '../http/medicalReports.http'

export const useMedicalReportFormStatus = ({
    form,
    mutation
}: {
    form: UseFormReturn<MedicalReportValidationSchema>
    mutation: UseMutationResult<AxiosResponse, AxiosError, HttpMedicalReportPayload>
}): {
    isDisabled: boolean
    isPendingSubmit: boolean
    isPendingDraftSaving: boolean
    isDirty: boolean
} => {
    const project = useWatch({control: form.control, name: 'project'})
    const reportType = useWatch({control: form.control, name: 'reportType'})

    // Check if project is missing or an empty object
    const isMissingProject = useMemo(() => !project || Object.keys(project).length === 0, [project])

    // Determine whether the selected reportType is compliant with the project type
    const isTypeProjectCompliant = useMemo(() => {
        if (!reportType) return true

        const {keyName} = reportType
        const projectType = project?.type

        const nonCompliantCases: Record<string, boolean> = {
            start_continuous_project: isMissingProject || projectType == 'cyclic',
            // A cyclic project accepts a renewal report only if it was transformed or extended
            // (for a cyclic project, `renewedProjectId` denotes an extension).
            renew_continuous_project:
                isMissingProject ||
                (projectType == 'cyclic' && !project?.transformedProjectId && !project?.renewedProjectId),
            close_continuous_project: isMissingProject || projectType == 'cyclic',
            start_cyclical_project: isMissingProject || projectType == 'continuous',
            close_cyclical_project: isMissingProject || projectType == 'continuous',
            initial_project_assessment: isMissingProject,
            extension_request: isMissingProject
        }

        return !nonCompliantCases[keyName]
    }, [isMissingProject, project?.type, reportType])

    return {
        isDisabled: !isTypeProjectCompliant,
        isPendingSubmit: mutation.isPending && mutation.variables.status == 'signed',
        isPendingDraftSaving: mutation.isPending && mutation.variables.status == 'draft',
        isDirty: mutation.status == 'idle' && ObjectKeys(form.formState.dirtyFields).length > 0
    }
}
